import { useCallback, useEffect, useRef, useState } from 'react';

type Recognition = {
  lang: string;
  interimResults: boolean;
  continuous: boolean;
  start: () => void;
  stop: () => void;
  abort: () => void;
  onresult: ((e: { results: ArrayLike<ArrayLike<{ transcript: string }> & { isFinal: boolean }> }) => void) | null;
  onend: (() => void) | null;
  onerror: ((e: { error: string }) => void) | null;
};

function recognitionCtor(): (new () => Recognition) | null {
  if (typeof window === 'undefined') return null;
  const w = window as unknown as {
    SpeechRecognition?: new () => Recognition;
    webkitSpeechRecognition?: new () => Recognition;
  };
  return w.SpeechRecognition ?? w.webkitSpeechRecognition ?? null;
}

/** Browser speech recognition (Chrome, Edge, Safari incl. iPad). Unsupported browsers get a clear reason. */
export function useSpeech(onFinal: (text: string) => void) {
  const Ctor = recognitionCtor();
  const [listening, setListening] = useState(false);
  const [interim, setInterim] = useState('');
  const [error, setError] = useState<string | null>(null);
  const rec = useRef<Recognition | null>(null);
  const finalRef = useRef(onFinal);
  finalRef.current = onFinal;

  const stop = useCallback(() => rec.current?.stop(), []);
  const start = useCallback(() => {
    if (!Ctor) return;
    setError(null);
    setInterim('');
    const r = new Ctor();
    r.lang = navigator.language || 'en-US';
    r.interimResults = true;
    r.continuous = false;
    r.onresult = (e) => {
      let text = '';
      let final = false;
      for (let i = 0; i < e.results.length; i++) {
        text += e.results[i]![0]!.transcript;
        if (e.results[i]!.isFinal) final = true;
      }
      setInterim(text);
      if (final) finalRef.current(text.trim());
    };
    r.onerror = (e) =>
      setError(e.error === 'not-allowed' || e.error === 'service-not-allowed' ? 'Microphone permission was denied. Allow it in the browser to speak commands.' : e.error === 'no-speech' ? 'I did not hear anything.' : 'Voice input stopped.');
    r.onend = () => setListening(false);
    rec.current = r;
    setListening(true);
    r.start();
  }, [Ctor]);
  useEffect(() => () => rec.current?.abort(), []);
  return { supported: !!Ctor, listening, interim, error, start, stop };
}

/** Spoken replies for commands given by voice. */
export function speak(text: string) {
  try {
    if (!('speechSynthesis' in window)) return;
    window.speechSynthesis.cancel();
    const u = new SpeechSynthesisUtterance(text);
    u.rate = 1.03;
    window.speechSynthesis.speak(u);
  } catch {
    /* speech output is optional */
  }
}
